import React from 'react';
import { Points } from 'components/Points/Points';
import { OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';
import { BannerState } from './setupState';
import { BACK_TO_SUGGESTION, CopyParts, HIDE_LINK } from './setupCopy';

// The left column: tag line, title, reward pill, one sentence, one primary
// button (two in state I), one hint. Point values are <Points>, never text.

export interface FeaturedActions {
  onPrimary: () => void;
  onSecondary?: () => void;
  onBackToSuggestion: () => void;
  onHide?: () => void;
  onConfirmFollow?: () => void;
  onCancelFollow?: () => void;
}

interface Props {
  state: BannerState;
  copy: CopyParts;
  task: OnboardingTask | null;
  /** Rewards ready across the featured pick (the Claim-all sum, or the single task's reward) */
  rewardPoints: number;
  /** The pill is a "+" reward for an open task, a plain sum for a claim */
  rewardSigned: boolean;
  picked: boolean;
  busy: boolean;
  /** A social follow is in its "I followed" confirm step */
  confirmingKey: OnboardingTaskKey | null;
  progress: { current: number; target: number } | null;
  actions: FeaturedActions;
}

const FeaturedTask: React.FC<Props> = ({ state, copy, task, rewardPoints, rewardSigned, picked, busy, confirmingKey, progress, actions }) => {
  const isClaim = state === 'C' || state === 'H';
  const gold = isClaim;
  const confirming = !!confirmingKey && !!task && confirmingKey === task.taskKey;
  return (
    <div className="setup-featured" data-testid="setup-featured" data-state={state} data-task={task?.taskKey ?? ''}>
      <div className="setup-featured__tagrow">
        <span className={`setup-featured__tag${gold ? ' setup-featured__tag--gold' : ''}`} data-testid="setup-tag">
          {copy.tag}
        </span>
        {picked && (
          <button type="button" className="setup-featured__back" onClick={actions.onBackToSuggestion} data-testid="setup-back">
            {BACK_TO_SUGGESTION}
          </button>
        )}
      </div>
      <div className="setup-featured__titlerow">
        <h2 className="setup-featured__title" data-testid="setup-title">
          {copy.title}
        </h2>
        {rewardPoints > 0 && (
          <span className="setup-featured__pill" data-testid="setup-reward">
            <Points type="medium" points={rewardPoints} signed={rewardSigned} />
          </span>
        )}
      </div>
      <p className="setup-featured__body" data-testid="setup-body">
        {copy.body}
        {copy.bodyPoints !== undefined && (
          <>
            <Points type="inline" points={copy.bodyPoints} />
            {copy.bodyAfter}
          </>
        )}
      </p>
      {progress && (
        <div className="setup-progress" data-testid="setup-progress">
          <div className="setup-progress__track">
            <div className="setup-progress__fill" style={{ width: `${Math.min(100, (progress.current / progress.target) * 100)}%` }} />
          </div>
          <span className="setup-progress__label">
            {progress.current} / {progress.target}
          </span>
        </div>
      )}
      <div className="setup-featured__actions">
        {confirming ? (
          <>
            <button type="button" className="setup-btn setup-btn--primary" onClick={actions.onConfirmFollow} disabled={busy} data-testid="setup-confirm-follow">
              I followed
            </button>
            <button type="button" className="setup-btn setup-btn--ghost" onClick={actions.onCancelFollow} disabled={busy}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`setup-btn ${gold ? 'setup-btn--gold' : state === 'G' ? 'setup-btn--ghost' : 'setup-btn--primary'}`}
              onClick={actions.onPrimary}
              disabled={busy}
              data-testid="setup-primary"
            >
              {copy.button}
              {isClaim && (
                <span className="setup-btn__points">
                  <Points type="inline" points={rewardPoints} />
                </span>
              )}
            </button>
            {copy.secondaryButton && actions.onSecondary && (
              <button type="button" className="setup-btn setup-btn--ghost" onClick={actions.onSecondary} disabled={busy} data-testid="setup-secondary">
                {copy.secondaryButton}
              </button>
            )}
          </>
        )}
        {copy.hint && !confirming && (
          <span className="setup-featured__hint" data-testid="setup-hint">
            {copy.hint}
          </span>
        )}
        {copy.claimHint && !confirming && <span className="setup-featured__hint">{copy.claimHint}</span>}
      </div>
      {state === 'I' && actions.onHide && (
        <button type="button" className="setup-featured__hide" onClick={actions.onHide} data-testid="setup-hide-link">
          {HIDE_LINK}
        </button>
      )}
    </div>
  );
};

export default FeaturedTask;
